'use client';

import { Check } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import type { MarketingPricingCopy } from '@/i18n/marketing-pricing-copy';
import { cn } from '@/lib/utils';
import { MarketingCtaLabel } from '@/components/marketing-landing/marketing-cta-label';
import {
  resolveMarketingPlanPrice,
  type MarketingPricingFrequency,
} from './marketing-pricing-model';
import { SectionShell } from './section-shell';

export function MarketingPricingTiers({
  copy,
}: {
  copy: MarketingPricingCopy;
}) {
  const [frequency, setFrequency] =
    useState<MarketingPricingFrequency>('monthly');

  return (
    <section className="bg-marketing-surface w-full py-16">
      <SectionShell>
        <div className="flex flex-col items-center gap-3">
          <div
            role="group"
            aria-label={copy.billing.ariaLabel}
            className="border-marketing-border bg-marketing-bg relative isolate grid grid-cols-2 rounded-full border p-1"
          >
            <div
              aria-hidden="true"
              className={cn(
                'bg-marketing-panel pointer-events-none absolute inset-y-1 start-1 w-[calc(50%_-_0.25rem)] rounded-full transition-transform duration-200 ease-out motion-reduce:transition-none',
                frequency === 'annual'
                  ? 'translate-x-full rtl:-translate-x-full'
                  : 'translate-x-0'
              )}
            />
            {(['monthly', 'annual'] as const).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={frequency === option}
                onClick={() => setFrequency(option)}
                className={cn(
                  'focus-visible:outline-marketing-accent-strong relative z-10 cursor-pointer rounded-full px-5 py-2 text-sm font-medium [letter-spacing:-0.01em] transition-colors duration-200 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 motion-reduce:transition-none',
                  frequency === option
                    ? 'text-marketing-panel-fg'
                    : 'text-marketing-fg-muted hover:text-marketing-fg'
                )}
              >
                {option === 'monthly'
                  ? copy.billing.monthly
                  : copy.billing.yearly}
              </button>
            ))}
          </div>
          <p className="text-marketing-fg text-[clamp(0.75rem,0.9vw,0.8125rem)] leading-[1.45] font-medium">
            {copy.billing.savings}
          </p>
        </div>

        <div className="mt-10 grid gap-4 lg:mt-14 lg:grid-cols-3">
          {copy.plans.map((plan) => (
            <PricingPlanCard
              key={plan.key}
              copy={copy}
              frequency={frequency}
              plan={plan}
            />
          ))}
        </div>
      </SectionShell>
    </section>
  );
}

function PricingPlanCard({
  copy,
  frequency,
  plan,
}: {
  copy: MarketingPricingCopy;
  frequency: MarketingPricingFrequency;
  plan: MarketingPricingCopy['plans'][number];
}) {
  const price = resolveMarketingPlanPrice(plan, frequency);
  const featured = plan.key === 'pro';

  return (
    <article
      className={cn(
        'flex min-h-full flex-col rounded-2xl border p-6 md:rounded-3xl md:p-8',
        featured
          ? 'border-marketing-panel bg-marketing-panel text-marketing-panel-fg'
          : 'border-marketing-border bg-marketing-bg text-marketing-fg'
      )}
    >
      <h2 className="text-[clamp(1.2rem,1.1vw,1.5rem)] leading-[1.25] font-bold [letter-spacing:-0.02em]">
        {plan.name}
      </h2>
      <p
        className={cn(
          'mt-2 min-h-12 text-[clamp(0.75rem,0.9vw,0.8125rem)] leading-[1.45] text-pretty',
          featured ? 'text-marketing-panel-fg/90' : 'text-marketing-fg/80'
        )}
      >
        {plan.description}
      </p>

      <div className="mt-4 flex items-end gap-3 lg:mt-8">
        <span className="text-[clamp(1.5rem,3.4vw,2.75rem)] leading-[1.1] font-bold [letter-spacing:-0.03em]">
          {price.price}
        </span>
        {price.compareAt ? (
          <span
            className={cn(
              'pb-1 text-[clamp(0.9375rem,1.3vw,1.25rem)] leading-[1.35] font-semibold [letter-spacing:-0.015em] line-through',
              featured
                ? 'text-marketing-panel-fg/60'
                : 'text-marketing-fg-subtle'
            )}
          >
            {price.compareAt}
          </span>
        ) : null}
        <span
          className={cn(
            'pb-1 text-[clamp(0.75rem,0.9vw,0.8125rem)] leading-[1.45]',
            featured ? 'text-marketing-panel-fg/70' : 'text-marketing-fg-muted'
          )}
        >
          {copy.billing.perMonth}
        </span>
      </div>
      <p
        className={cn(
          'mt-2 min-h-10 text-[clamp(0.75rem,0.9vw,0.8125rem)] leading-[1.45]',
          featured ? 'text-marketing-panel-fg/70' : 'text-marketing-fg-muted'
        )}
      >
        {frequency === 'annual'
          ? copy.billing.billedAnnually
          : plan.introductoryMonthlyPrice
            ? plan.introductoryLabel
            : copy.billing.billedMonthly}
      </p>

      <dl
        className={cn(
          'mt-3 grid grid-cols-2 gap-4 border-y py-5 lg:mt-7',
          featured ? 'border-marketing-panel-fg/20' : 'border-marketing-border'
        )}
      >
        <div>
          <dt
            className={cn(
              'text-[clamp(0.6875rem,0.9vw,0.8125rem)] leading-[1.3] font-semibold [letter-spacing:-0.01em]',
              featured
                ? 'text-marketing-panel-fg/60'
                : 'text-marketing-fg-subtle'
            )}
          >
            {frequency === 'annual'
              ? copy.metrics.annualCredits
              : copy.metrics.credits}
          </dt>
          <dd className="mt-2 text-[clamp(0.875rem,1.1vw,1rem)] leading-[1.25] font-bold [letter-spacing:-0.02em]">
            {frequency === 'annual' ? plan.annualCredits : plan.credits}
          </dd>
        </div>
        <div>
          <dt
            className={cn(
              'text-[clamp(0.6875rem,0.9vw,0.8125rem)] leading-[1.3] font-semibold [letter-spacing:-0.01em]',
              featured
                ? 'text-marketing-panel-fg/60'
                : 'text-marketing-fg-subtle'
            )}
          >
            {copy.metrics.driveSpace}
          </dt>
          <dd className="mt-2 text-[clamp(0.875rem,1.1vw,1rem)] leading-[1.25] font-bold [letter-spacing:-0.02em]">
            {plan.driveSpace}
          </dd>
        </div>
      </dl>

      <ul className="mt-7 flex flex-1 flex-col gap-3">
        {plan.features.map((feature) => (
          <li
            key={feature}
            className="flex items-start gap-3 text-[clamp(0.75rem,0.9vw,0.8125rem)] leading-[1.45]"
          >
            <Check aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <Link
        href={plan.ctaHref}
        className={cn(
          'group focus-visible:outline-marketing-accent-strong mt-8 flex min-h-11 items-center justify-center rounded-lg text-center text-sm font-medium [letter-spacing:-0.01em] transition-colors outline-none focus-visible:outline-2 focus-visible:outline-offset-2',
          featured
            ? 'bg-marketing-bg text-marketing-fg hover:bg-marketing-bg/85'
            : 'bg-marketing-panel text-marketing-panel-fg hover:bg-marketing-panel/85'
        )}
      >
        <MarketingCtaLabel>{plan.ctaLabel}</MarketingCtaLabel>
      </Link>
    </article>
  );
}
